import { ArticleService } from 'api/services/ArticleService'
import { applyLinkToSelection, markAttributesForHref } from 'common/Prose/utils/link'
import { createArticleMention, maybeGetArticleKey, maybeGetSectionLink } from 'common/Prose/utils/smartLinks/utils'
import { REACT_APP_IFRAMELY_API_KEY } from 'env'
import { TLDs } from 'global-tld-list'
import { getLocationUrl } from 'hooks/useHistory'
import { calculateHeadings } from 'hooks/useTableOfContents'
import { Schema, Slice } from 'prosemirror-model'
import { NodeSelection, TextSelection, Transaction } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'
import { QueryClient } from 'react-query'
import { GITHUB_INTEGRATION_REGEX, INTEGRATION_REGEXES, JIRA_INTEGRATION_REGEX } from 'screens/ProseEdit/utils'
import { UrlString, isUrlString, urlString } from 'types/urlString'
import { reportError } from '../../../utils/error'

export function handlePaste(queryClient: QueryClient) {
  return (view: EditorView, e: Event, slice: Slice) => {
    const {
      state: { selection, tr },
    } = view
    /**
     * When pure text is selected and the user pastes a link, instead of replacing the text with
     * the URL text, make the text into a link
     *
     * When user pastes a link at a pos (no selection), the link text is changed into a URL
     */
    if (!(selection instanceof TextSelection)) {
      return false
    }
    if (
      slice.content.childCount !== 1 ||
      (slice.content.firstChild?.childCount !== 1 && !slice.content.firstChild?.isText)
    ) {
      return false
    }

    const maybeURL = slice.content.firstChild.textContent ?? ''

    if (!isUrlString(maybeURL)) {
      return false
    }
    const maybeTld = extractTld(maybeURL)
    if (maybeTld && !TLDs.includes(maybeTld.toLowerCase())) {
      return false
    }

    const { empty, from, to } = selection

    const selectedText = empty ? maybeURL : selection.content().content.firstChild?.textContent
    if (!selectedText) {
      return false
    }

    e.preventDefault()

    if (maybeConvertToSectionLink(selectedText, view)) {
      return true
    }

    if (maybeConvertToSmartLink(selectedText, view, queryClient)) {
      return true
    }

    if (empty) {
      createHyperLink(view.state.schema, maybeURL, selectedText, from, tr)
    } else {
      tr.addMark(from, to, view.state.schema.marks.link.create(markAttributesForHref(maybeURL)))
    }

    dispatchTransaction(view, tr)
    return true
  }
}

const createHyperLink = (schema: Schema, url: string, selectedText: string, from: number, tr: Transaction) => {
  return tr.insert(from, schema.text(selectedText, [schema.marks.link.create(markAttributesForHref(url))]))
}

const dispatchTransaction = (view: EditorView, tr: Transaction) => {
  /*
    Normally, Prose Mirror sets this itself, but when we dispatch the tr inside of a
    a plugin's handlePaste, the meta isn't set properly
  */
  tr.setMeta('paste', true)
  view.dispatch(tr)
}

const extractTld = (urlString: UrlString): string | undefined =>
  urlString.match(/\.\w+($|\/)/)?.[0]?.replace(/\.|\//g, '')

const maybeConvertToSectionLink = (selectedText: string, view: EditorView): boolean => {
  const url = urlString(selectedText)
  const key = maybeGetArticleKey(url)
  const { href } = getLocationUrl()
  const isSameArticle = isUrlString(href) && key === maybeGetArticleKey(href)
  const sectionHash = maybeGetSectionLink(url)
  if (!key || !url || !isSameArticle || !sectionHash) return false
  // NOTICE: The context for calculateHeadings is the whole current document
  //         It could become a problem if we ever side-by-side docs
  //         Using `view.dom` would break it for doc comments with section links
  const headings = calculateHeadings()
  const sectionId = sectionHash.slice(1)
  for (const { blockId, title } of headings) {
    if (blockId === sectionId) {
      applyLinkToSelection(view, { link: `#${blockId}`, text: title ?? selectedText })
      return true
    }
  }
  return false
}

const maybeConvertToSmartLink = (selectedText: string, view: EditorView, queryClient: QueryClient): boolean => {
  const url = urlString(selectedText)
  const key = maybeGetArticleKey(url)
  const sectionHash = maybeGetSectionLink(url)
  if (!key || !url) return false

  // if it's an almanac doc, return true and create the smart link asynchronously
  ArticleService.getTitlesFromKeys(queryClient, [key])
    .then((titles) => {
      const title = titles?.[key]
      if (!title) throw new Error()

      const node = createArticleMention(view, key, title, sectionHash?.slice(1))
      const tr = view.state.tr.insert(view.state.selection.from, node)
      dispatchTransaction(view, tr)
    })
    .catch(() => {
      // if this fails, create a normal link
      const tr = createHyperLink(view.state.schema, url, selectedText, view.state.selection.from, view.state.tr)
      dispatchTransaction(view, tr)
    })

  return true
}

const queryIframely = async (inputValue: string) => {
  const response = await fetch(`https://iframe.ly/api/iframely?key=${REACT_APP_IFRAMELY_API_KEY}&url=${inputValue}`)
  const parsedResponse = await response.json()
  return parsedResponse
}

export const maybeConvertToEmbed = (view: EditorView, selectedText: string, from: number, tr: Transaction): boolean => {
  const { schema } = view.state
  const githubRegexResult = GITHUB_INTEGRATION_REGEX.exec(selectedText)

  // NOTE: ProseInput does not contain github_embed in its schema, so this works
  if ('github_embed' in schema.nodes && githubRegexResult && githubRegexResult.groups?.resourceName) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    let type = githubRegexResult.groups.resourceName!
    if (type === 'issues') type = 'issue'
    tr.insert(
      from,
      schema.nodes.github_embed.create({ link: selectedText, type: type as 'pull' | 'issue', mode: 'frame' }),
    )
    return true
  }

  const jiraRegexResult = JIRA_INTEGRATION_REGEX.exec(selectedText)

  // NOTE: ProseInput does not contain jira_embed in its schema, so this works
  if ('jira_embed' in schema.nodes && jiraRegexResult && jiraRegexResult.groups?.id) {
    tr.insert(
      from,
      schema.nodes.jira_embed.create({
        link: selectedText,
        type: 'issue',
        mode: 'frame',
        issueId: jiraRegexResult.groups.id,
      }),
    )
    return true
  }

  const isIntegration = INTEGRATION_REGEXES.some((regex): boolean => {
    return regex.exec(selectedText) as unknown as boolean
  })

  if ('iframe' in schema.nodes && isIntegration) {
    const { from } = view.state.selection
    const createLink = () => {
      const tr = view.state.tr
      createHyperLink(view.state.schema, selectedText, selectedText, from, tr)
      view.dispatch(tr)
    }
    queryIframely(selectedText)
      .then((response) => {
        if (!response.html) return createLink()
        const tr = view.state.tr
        tr.insert(
          from,
          schema.nodes.iframe.create({
            url: selectedText,
            type: 'url',
            mode: 'frame',
            srcdoc: response.html,
          }),
        )
        tr.setSelection(NodeSelection.create(tr.doc, view.state.selection.from + 1))
        view.dispatch(tr)
      })
      .catch((error) => {
        reportError(error)
        createLink()
      })

    return true
  }

  return false
}
